import React from "react";
import { Link } from "react-router-dom";
import { GraduationCap, Mic, BookOpen, HeartHandshake, Award, Users2, Newspaper, Sparkles, ExternalLink } from "lucide-react";
import DOMPurify from "dompurify";
const stripNbsp = (s) => (s || "")
  .replace(/\u00a0/g, " ")
  .replace(/&nbsp;/gi, " ")
  .replace(/&#160;/gi, " ")
  .replace(/&#xa0;/gi, " ");
const Html = ({ html, className = "" }) => (
  <div className={className} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(stripNbsp(html)) }} />
);

const APPROCCIO = [
  { title: "Affermativo", text: "Riconosco e valorizzo la pluralità delle esperienze umane senza patologizzare identità, orientamenti, pratiche consensuali o forme relazionali non normative." },
  { title: "Sex-positive", text: "Considero la sessualità come una dimensione complessa e legittima della persona. Lavoro su desiderio, consenso, comunicazione, piacere, corpo e vergogna." },
  { title: "Relazionale", text: "Presto attenzione ai legami, ai pattern comunicativi, ai bisogni reciproci e ai sistemi di appartenenza in cui le persone sono immerse." },
  { title: "Orientato al consenso", text: "Il consenso non è assenza di coercizione: è pratica continua di ascolto, negoziazione e cura reciproca." },
  { title: "Trauma-informed", text: "Lavoro con consapevolezza dell'impatto delle esperienze di invalidazione, delle ferite relazionali e del minority stress. Non sono traumatologə: non tratto il trauma complesso o il PTSD come specializzazione." },
  { title: "Neurodivergent-friendly", text: "Lo studio è accessibile a persone neurodivergenti già diagnosticate, con attenzione a comunicazione e ritmo del percorso. Non effettuo valutazione diagnostica né percorsi specialistici sulle neurodivergenze." },
  { title: "Transfemminista intersezionale", text: "Uno sguardo che tiene conto di come genere, classe, razzializzazione, disabilità e altre dimensioni si intersecano nell'esperienza — dentro e fuori la stanza clinica." },
];

const FORMAZIONE = [
  "Laurea triennale in Scienze e Tecniche Psicologiche — Università degli Studi di Genova",
  "Laurea Magistrale in Psicologia Clinica — Università degli Studi di Bergamo (110 e lode)",
  "Master universitario di II livello in «Valutazione Multidimensionale Psicologica e Tecniche Orientate al Cambiamento» — Università degli Studi di Bergamo (2017–2018): titolo di Counselor",
  "Master in Sessuologia Clinica — Giunti Psychometrics",
  "Iscritto all'Albo degli Psicologi della Lombardia — n. 22141 (dal 27/01/2020)",
  "Formazione continua in psicologia critica, studi di genere, teoria queer e postcoloniale",
  "Aggiornamento permanente in consulenza affermativa alle persone non monogame e alle sessualità plurali",
];

const DOCENZE = [
  { where: "PESI · FCP — Formazione Continua in Psicologia", role: "Docente Master Alta Formazione «Clinica dei nuovi legami amorosi e affettivi»", detail: "Modulo online «La consulenza con le persone non monogame» (50 ECM). Dal maggio 2024.", href: null },
  { where: "Giunti Psychometrics", role: "Docente al Master di Sessuologia", detail: "Formazione di psicologi/ə e professionistə su consulenza affermativa e non monogamie consensuali.", href: null },
  { where: "Istituto Italiano di Sessuologia Scientifica", role: "Docente", detail: "Interventi formativi su sessualità plurali, identità di genere, LGBTQIA+ e configurazioni relazionali non normative.", href: null },
];

const DIVULGAZIONE = [
  { title: "@hello_policose", detail: "Progetto di divulgazione psicologica su Instagram — dal 2019. Contenuti su relazioni non monogame, poliamore, anarchia relazionale, sessualità, consenso, identità, neurodivergenze e salute mentale con approccio transfemminista intersezionale.", href: "https://www.instagram.com/hello_policose/" },
  { title: "Sexpert · Control Italia", detail: "Fai parte del team di espertə di Control per la divulgazione sesso-positiva su salute sessuale e consenso.", href: null },
  { title: "YouthLab · Club Silencio", detail: "Collaborazione al progetto «Storie della Buonanotte» dell'Osservatorio Giovanile Club Silencio.", href: null },
  { title: "Educazione sesso-affettiva sul territorio", detail: "Interventi con associazioni e realtà territoriali su educazione sessuo-affettiva rivolta ad adolescentə, giovani adultə e comunità.", href: null },
];

const PUBBLICAZIONI = [
  { title: "Un poliamore così grande. Perché non c'è un solo modo di amare", where: "Edizioni Sonda, 2023", detail: "Saggio divulgativo sulle relazioni non monogame consensuali: teoria, pratica, decostruzione dei bias, cultura del consenso.", href: "https://amzn.eu/d/03zC1L3o", cta: "Acquista su Amazon" },
  { title: "Intimità rivoluzionarie e cura collettiva: oltre i binarismi e le normatività degli affetti", where: "Varchi Rivista — Tracce per la Psicoanalisi", detail: "Saggio su binarismo amore/amicizia, amatonormatività, relazioni queerplatoniche, anarchia relazionale, famiglia queer e prassi terapeutica non patologizzante.", href: "https://varchirivista.it/intimita-rivoluzionarie-e-cura-collettiva-oltre-i-binarismi-e-le-normativita-degli-affetti/", cta: "Leggi l'articolo" },
];

const RICONOSCIMENTI = [
  { title: "Premio Chiara Callerame — 2023", detail: "Conferito dalla Consulta per i Diritti Umani dell'Ordine degli Psicologi della Lombardia. Riconoscimento per il contributo professionale su temi di diritti, minoranze e salute mentale.", image: "https://customer-assets-4nw71qhi.emergentagent.net/job_modern-psychology/artifacts/eb2yh9kz_5C24601B-EE54-4802-AA48-00A5C9E8866D.jpeg" },
];

export default function ChiSono() {
  // Rich text fallbacks (kept as HTML strings so Quill and default markup coexist)
  const D = {
    heroImage: "/images/portrait.jpeg",
    heroSubtitle: "Psicologə · Consulente in sessuologia · Autrice · Docente · Divulgatrice",
    heroIntro1: "<p>Mi occupo di persone e relazioni che abitano le <em>soglie</em> e i <em>margini</em> dei modelli dominanti di identità, corpo, desiderio e affettività. Prendo in prestito questi concetti da pensatrici come <strong>Michela Murgia</strong> e <strong>bell hooks</strong> per descrivere quegli spazi di attraversamento in cui le norme vengono interrogate, ridefinite e trasformate. Il mio lavoro accompagna chi desidera esplorare la propria sessualità, i propri orientamenti e le proprie configurazioni relazionali all'interno di uno spazio non giudicante, informato sulle esperienze queer, sulle non monogamie e sulle diverse forme di intimità.</p>",
    heroIntro2: "<p>Non considero la terapia come un processo di normalizzazione della persona, ma come uno spazio di esplorazione, consapevolezza e costruzione di strumenti. L'obiettivo non è portare chi ho davanti verso un modello prestabilito di vita, relazione o identità, ma aiutare a capire cosa è autentico e sostenibile per sé.</p>",
    posizTitle: `Da dove parlo? <em>Espertə per professione ed espertə per esperienza.</em>`,
    posizBody: `<p>Ho una formazione accademica, un'iscrizione all'Albo degli Psicologi, anni di pratica clinica. Ma so anche che la mia expertise non viene solo da lì.</p><p>Sono una persona neurodivergente, transgender, non monogama, queer. Sono una persona che ha attraversato questi territori da un posizionamento incarnato, non solo che li ha studiati. E riconosco che questa esperienza, situata nel mio corpo e nella mia vita, è arricchente: mi permette di riconoscere, dentro la stanza clinica, quel <em>Minority Stress</em> che spesso è invisibilizzato dalle normatività che attraversano i nostri sistemi di potere.</p><p>Ma devo anche nominare i miei privilegi. Sono una persona bianca, della classe medio-borghese, che ha avuto accesso a risorse economiche, culturali, a uno spazio sociale che non tutti hanno. E questi privilegi cambiano completamente da dove parlo, quanto peso e potere hanno le mie parole.</p><blockquote><em>«[…] il luogo da dove parlo, lo spazio di enunciazione non riguarda solo il mio posizionamento ma anche la posizione del mio corpo». — Borghi, 2020</em></blockquote><p>Cito Borghi perché condivido il pensiero per cui la ricerca parte da un corpo, da un luogo specifico. Quando parliamo di te, di noi, della relazione che costruiamo nella stanza di terapia, non lo facciamo da nessun luogo neutro. Parto da qui: consapevole che la mia conoscenza è sempre parziale, sempre situata <em>(Haraway, 1988)</em>, sempre attraversata dai miei punti ciechi. E quando aggiungo l'emozione, quando riconosco quello che sento nel lavorare con te, la scrittura non si maschera da scienza distaccata, ma si mostra per quello che è: <strong>una pratica politica, consapevole di sé</strong>.</p><h3>L'approccio centrato sulla persona</h3><p>C'è un'altra cosa: <strong>la persona è esperta di sé stessa</strong>. Come diceva Carl Rogers, tu conosci la tua vita, il tuo corpo, quello che ti muove. Io sono qui per ascoltare da quella posizione, quella di chi sa di non sapere. La mia expertise è nel fare spazio perché la tua emerga.</p><p>In una pratica clinica affermativa, questo riconoscimento è la base per un lavoro esplorativo non giudicante che restituisca <em>ri-conoscimento</em> di sé.</p>`,
    divulgImage: "/images/workshop.jpeg",
    pubImageCover: "/images/book-cover.png",
    pubImageSigning: "/images/signing.jpeg",
  };
  // List fallbacks
  const formazione = FORMAZIONE.map((t) => ({ text: t }));
  const docenze = DOCENZE;
  const divulgazione = DIVULGAZIONE;
  const pubblicazioni = PUBBLICAZIONI;
  const riconoscimenti = RICONOSCIMENTI;

  return (
    <div data-testid="page-chi-sono">
      {/* HERO */}
      <section className="container-editorial py-16 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4">
            <div className="aspect-[3/4] rounded-2xl overflow-hidden border border-viola/10 shadow-[0_30px_60px_-30px_rgba(44,20,69,0.35)]">
              <img src={D.heroImage} alt="Ritratto di Dania Piras" className="h-full w-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-8">
            <span className="pill">Chi sono</span>
            <h1 className="mt-4 font-display text-5xl sm:text-6xl tracking-tighter leading-none">Dania (Dan) Piras</h1>
            <p className="mt-4 text-lg text-viola/60">{D.heroSubtitle}</p>
            <Html html={D.heroIntro1} className="mt-8 text-xl text-viola/85 leading-relaxed rich-body cms-para" />
            <Html html={D.heroIntro2} className="mt-6 text-viola/80 leading-relaxed rich-body cms-para" />
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/consulenze" className="btn-primary" data-testid="chi-cta-consulenze">Prenota una consulenza</Link>
              <a href="https://www.instagram.com/hello_policose/" target="_blank" rel="noopener noreferrer" className="btn-ghost" data-testid="chi-cta-ig">Segui @hello_policose</a>
            </div>
          </div>
        </div>
      </section>

      {/* APPROCCIO */}
      <section className="bg-lilla/40 border-y border-viola/10">
        <div className="container-editorial py-20">
          <div className="max-w-3xl">
            <span className="pill">Posizionamento</span>
            <Html html={D.posizTitle} className="mt-4 font-display text-4xl sm:text-5xl tracking-tight leading-tight cms-heading" />
          </div>

          <div className="mt-10 max-w-3xl space-y-6 text-lg leading-relaxed text-viola/85 chisono-posiz rich-body">
            <Html html={D.posizBody} />
          </div>

          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {APPROCCIO.map((a) => (
              <div key={a.title} className="card-flat" data-testid={`approach-${a.title.toLowerCase()}`}>
                <p className="text-xs font-semibold uppercase tracking-widest text-fucsia">{a.title}</p>
                <p className="mt-3 text-viola/80 leading-relaxed">{a.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORMAZIONE */}
      <section className="container-editorial py-20">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="grid h-12 w-12 place-items-center rounded-full bg-giallo text-viola"><GraduationCap size={20} /></div>
            <h2 className="mt-6 font-display text-3xl sm:text-4xl">Formazione</h2>
            <p className="mt-4 text-viola/70">Percorso accademico e clinico in psicologia, sessuologia e studi critici.</p>
          </div>
          <ul className="lg:col-span-8 space-y-3">
            {formazione.map((f, i) => (
              <li key={f.__k || f.text || i} className="flex gap-4 items-start rounded-xl border border-viola/10 bg-white p-5">
                <span className="text-fucsia font-semibold text-sm mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-viola/85">{f.text}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* DOCENZE */}
      <section className="bg-viola text-cream">
        <div className="container-editorial py-20">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="grid h-12 w-12 place-items-center rounded-full bg-giallo text-viola"><Users2 size={20} /></div>
              <h2 className="mt-6 font-display text-3xl sm:text-4xl">Docenze</h2>
              <p className="mt-4 text-cream/70">Formazione di psicologi/ə, sessuologi/ə e professionistə della salute mentale.</p>
              <div className="mt-8 rounded-2xl overflow-hidden border border-cream/15 aspect-[4/3]">
                <img src="/images/panel.jpeg" alt="Dania Piras durante un intervento pubblico" className="h-full w-full object-cover" />
              </div>
            </div>
            <div className="lg:col-span-8 space-y-4">
              {docenze.map((d, i) => (
                <div key={d.__k || d.where || i} className="rounded-xl border border-cream/15 p-6" data-testid={`docenza-${i}`}>
                  <p className="text-xs uppercase tracking-widest text-giallo font-semibold">{d.where}</p>
                  <h3 className="mt-2 font-display text-2xl">{d.role}</h3>
                  <Html html={d.detail || ""} className="mt-2 text-cream/80 rich-body" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DIVULGAZIONE */}
      <section className="container-editorial py-20">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="grid h-12 w-12 place-items-center rounded-full bg-fucsia text-white"><Mic size={20} /></div>
            <h2 className="mt-6 font-display text-3xl sm:text-4xl">Divulgazione</h2>
            <p className="mt-4 text-viola/70">Costruire cultura psicologica accessibile è per me parte integrante del prendersi cura.</p>
            <div className="mt-8 rounded-2xl overflow-hidden border border-viola/10 aspect-[4/3]">
              <img src={D.divulgImage} alt="Workshop Poliamore Policose al festival" className="h-full w-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
            {divulgazione.map((d, i) => (
              <div key={d.__k || d.title || i} className="card-flat" data-testid={`div-${i}`}>
                <h3 className="font-display text-xl">{d.title}</h3>
                <Html html={d.detail || ""} className="mt-2 text-sm text-viola/75 rich-body" />
                {d.href && <a href={d.href} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-fucsia">Vai <ExternalLink size={12} /></a>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PUBBLICAZIONI */}
      <section className="bg-arancio/30 border-y border-viola/10">
        <div className="container-editorial py-20">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="grid h-12 w-12 place-items-center rounded-full bg-viola text-cream"><BookOpen size={20} /></div>
              <h2 className="mt-6 font-display text-3xl sm:text-4xl">Pubblicazioni</h2>
              <p className="mt-4 text-viola/70">Libri e saggi su relazioni non monogame, sessualità e clinica queer-affirmative.</p>
              <div className="mt-8 grid grid-cols-2 gap-3">
                <div className="rounded-xl overflow-hidden aspect-[3/4] shadow-lg">
                  <img src={D.pubImageCover} alt="Copertina del libro Un poliamore così grande" className="h-full w-full object-cover" />
                </div>
                <div className="rounded-xl overflow-hidden aspect-[3/4] shadow-lg">
                  <img src={D.pubImageSigning} alt="Firmacopie al festival" className="h-full w-full object-cover" />
                </div>
              </div>
            </div>
            <div className="lg:col-span-8 space-y-4">
              {pubblicazioni.map((p, i) => (
                <div key={p.__k || p.title || i} className="rounded-xl border border-viola/10 bg-white p-6" data-testid={`pub-${i}`}>
                  <p className="text-xs uppercase tracking-widest text-viola/60 font-semibold">{p.where}</p>
                  <h3 className="mt-2 font-display text-2xl leading-tight">{p.title}</h3>
                  <Html html={p.detail || ""} className="mt-3 text-viola/80 rich-body" />
                  {p.href && <a href={p.href} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-fucsia">{p.cta || "Scopri"} <ExternalLink size={12} /></a>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AFFILIAZIONI PROFESSIONALI */}
      <section className="container-editorial py-20">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
                    <div className="grid h-12 w-12 place-items-center rounded-full bg-fucsia text-cream"><Users2 size={20} /></div>
            <h2 className="mt-6 font-display text-3xl sm:text-4xl">Affiliazioni professionali</h2>
            <p className="mt-4 text-viola/70">Realtà della rete GSRD di cui faccio parte attivamente.</p>
          </div>
          <div className="lg:col-span-8">
            <a
              href="https://associazione-asterisco.it"
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-2xl border-2 border-viola/10 hover:border-fucsia/60 transition-colors overflow-hidden bg-white group"
              data-testid="affiliazione-asterisco"
            >
              <div className="grid sm:grid-cols-[180px_1fr] gap-6 p-6 sm:p-8 items-start">
                <div className="rounded-xl bg-cream/60 p-6 flex items-center justify-center aspect-square sm:aspect-auto">
                  <img
                    src="/images/asterisco-logo.png"
                    alt="Logo Associazione Asterisco"
                    className="w-full h-auto max-w-[140px] mx-auto"
                    loading="lazy"
                  />
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-fucsia">Team member · Supervisora</p>
                  <h3 className="mt-2 font-display text-2xl leading-tight group-hover:text-fucsia transition-colors">
                    Associazione Asterisco
                  </h3>
                  <p className="mt-3 text-sm text-viola/80 leading-relaxed">
                    Team di professionistɜ della salute composto principalmente da persone queer, trans, neurodivergenti, non monogame, kinky. Obiettivo: promuovere il benessere bio-psico-sociale e sessuale delle identità marginalizzate. Faccio parte del team dello <span className="font-semibold">Studio Asterisco</span> — servizio di consulenza psicologica, neuropsicologica, medica, psichiatrica, dietistica e sessuologica affermativa per persone GSRD (Gender, Sex, Relationship Diversities) con attenzione ai funzionamenti neurodivergenti.
                  </p>
                  <p className="mt-3 text-xs text-viola/60">
                    Sede: Corso Monte Grappa 11, Torino — Attivə dal 2019.
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-fucsia group-hover:gap-2 transition-all">
                    Visita il sito <ExternalLink size={12} />
                  </span>
                </div>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* COLLABORAZIONI & MEDIA */}
      <section className="container-editorial py-20">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="grid h-12 w-12 place-items-center rounded-full bg-giallo text-viola"><Sparkles size={20} /></div>
            <h2 className="mt-6 font-display text-3xl sm:text-4xl">Collaborazioni & media</h2>
            <p className="mt-4 text-viola/70">Progetti editoriali, media outlet e realtà territoriali.</p>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
            <div className="card-flat"><p className="font-display text-xl">Edizioni Sonda</p><p className="mt-2 text-sm text-viola/70">Casa editrice del mio primo libro.</p></div>
            <div className="card-flat"><p className="font-display text-xl">Varchi Rivista</p><p className="mt-2 text-sm text-viola/70">Autrice di saggi su psicologia critica e non monogamie.</p></div>
            <div className="card-flat"><p className="font-display text-xl">Control Italia · Sexpert</p><p className="mt-2 text-sm text-viola/70">Team di espertə per contenuti sesso-positivi.</p></div>
            <a
              href="https://www.artribune.com/mostre-evento-arte/club-silencio-storie-della-buona-notte/"
              target="_blank"
              rel="noopener noreferrer"
              className="card-flat block group"
              data-testid="collab-club-silencio"
            >
              <p className="font-display text-xl group-hover:text-fucsia transition-colors">Club Silencio · YouthLab</p>
              <p className="mt-2 text-sm text-viola/70">Progetto «Storie della Buonanotte» — approfondimento su Artribune.</p>
              <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-fucsia">Leggi l'articolo <ExternalLink size={12} /></span>
            </a>
          </div>
        </div>
      </section>

      {/* PREMI */}
      <section className="bg-viola text-cream">
        <div className="container-editorial py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="grid h-12 w-12 place-items-center rounded-full bg-giallo text-viola"><Award size={20} /></div>
            <h2 className="mt-6 font-display text-3xl sm:text-4xl">Premi e riconoscimenti</h2>
          </div>
          <div className="lg:col-span-8 space-y-4">
            {riconoscimenti.map((r, i) => (
              <div key={r.__k || r.title || i} className="rounded-xl border border-cream/15 p-6 flex flex-col sm:flex-row gap-6 items-start">
                {r.image && (
                  <div className="w-full sm:w-40 shrink-0 rounded-lg overflow-hidden aspect-[3/4] bg-cream/10">
                    <img src={r.image} alt={r.title} className="h-full w-full object-cover" loading="lazy" />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="font-display text-xl">{r.title}</h3>
                  <Html html={r.detail || ""} className="mt-2 text-cream/80 rich-body" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-editorial py-20 text-center">
        <h2 className="font-display text-4xl sm:text-5xl tracking-tight max-w-2xl mx-auto">Se ti ritrovi in quello che leggi, possiamo lavorare insieme.</h2>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/consulenze" className="btn-primary">Prenota una consulenza</Link>
          <Link to="/contatti" className="btn-ghost">Scrivimi</Link>
        </div>
      </section>
    </div>
  );
}
